/**
 * Skills Manager Component
 * MPAD Implementation: Phase 3 - Skills-Based Routing Management
 * 
 * Comprehensive skills management interface with progressive disclosure
 * following Maya's UX patterns for complex skill taxonomy and vehicle assignment.
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../../services/api';
import { useSkills } from '../../hooks/useSkills';
import { useVehicles } from '../../hooks/useVehicles';
import { useJobs } from '../../hooks/useJobs';
import type { Skill as SkillType } from '../../hooks/useSkills';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Chip,
  Autocomplete,
  Slider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Tabs,
  Tab
} from '@mui/material';
import {
  Psychology,
  Build,
  People,
  Assignment,
  Analytics,
  NavigateNext,
  NavigateBefore,
  Add,
  Edit,
  Delete,
  Check,
  Warning,
  Info,
  Star,
  StarBorder,
  ExpandMore,
  Visibility,
  Speed,
  School,
  Security,
  LocalShipping,
  Verified,
  CheckCircle,
  Error,
  Schedule,
  TrendingUp,
  Assessment
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// ===============================================
// TYPES AND INTERFACES
// ===============================================

interface Skill {
  id: number;
  skill_code: string;
  skill_name: string;
  description?: string;
  category: string;
  subcategory?: string;
  parent_skill_id?: number;
  skill_type: 'capability' | 'certification' | 'equipment' | 'restriction' | 'preference';
  requires_certification: boolean;
  certification_authority?: string;
  certification_expiry_required: boolean;
  vroom_skill_id?: number;
  project_id?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  usage_count?: number;
}

interface Vehicle {
  id: number;
  name: string;
  license_plate: string;
  is_active: boolean;
  skills?: VehicleSkill[];
}

interface VehicleSkill {
  id: number;
  vehicle_id: number;
  skill_id: number;
  skill: Skill;
  proficiency_level: number;
  certification_number?: string;
  certification_expiry_date?: string;
  verified: boolean;
  notes?: string;
  verified_by?: string;
  verified_at?: string;
  created_at: string;
  updated_at: string;
}

interface Job {
  id: number;
  reference: string;
  description?: string;
  required_skills?: JobSkillRequirement[];
}

interface JobSkillRequirement {
  id: number;
  job_id: number;
  skill_id: number;
  skill: Skill;
  minimum_proficiency_level: number;
  is_mandatory: boolean;
  requirement_source: string;
  reasoning?: string;
}

interface SkillFormData {
  skill_code: string;
  skill_name: string;
  description?: string;
  category: string;
  subcategory?: string;
  parent_skill_id?: number;
  skill_type: 'capability' | 'certification' | 'equipment' | 'restriction' | 'preference';
  requires_certification: boolean;
  certification_authority?: string;
  certification_expiry_required: boolean;
  certification_expiry_date?: string;
  vroom_skill_id?: number;
  is_active?: boolean;
  project_id?: number;
}

interface ValidationResult {
  can_assign: boolean;
  missing_skills: Array<{
    skill_id: number;
    skill_name: string;
    skill_code: string;
    required_level: number;
    current_level: number;
    is_mandatory: boolean;
    certification_expired?: boolean;
  }>;
  warnings: Array<{
    type: string;
    message: string;
  }>;
}

interface SkillsManagerProps {
  projectId: number;
  selectionMode?: boolean;
  onSkillsUpdate?: () => void;
}

type ManagerStep = 'taxonomy' | 'assignments' | 'validation' | 'analytics' | 'review';

const MANAGER_STEPS: { id: ManagerStep; label: string; icon: React.ReactNode }[] = [
  { id: 'taxonomy', label: 'Skills Taxonomy', icon: <Psychology /> },
  { id: 'assignments', label: 'Vehicle Assignment', icon: <Assignment /> },
  { id: 'validation', label: 'Skill Validation', icon: <Verified /> },
  { id: 'analytics', label: 'Analytics', icon: <Analytics /> },
  { id: 'review', label: 'Review', icon: <Check /> }
];

const SKILL_CATEGORIES = [
  'Technical', 'Physical', 'Customer Service', 'Safety', 'Certification',
  'Equipment', 'Transport', 'Logistics', 'Security', 'Emergency'
];

const SKILL_TYPES = [
  { value: 'capability', label: 'Capability', description: 'A skill or ability' },
  { value: 'certification', label: 'Certification', description: 'Official certification required' },
  { value: 'equipment', label: 'Equipment', description: 'Vehicle equipment or feature' },
  { value: 'restriction', label: 'Restriction', description: 'Access or operational restriction' },
  { value: 'preference', label: 'Preference', description: 'Preferred but not required' }
];

const PROFICIENCY_LEVELS = [
  { value: 1, label: 'Beginner', color: '#f44336', description: 'Learning the basics' },
  { value: 2, label: 'Basic', color: '#ff9800', description: 'Basic understanding' },
  { value: 3, label: 'Novice', color: '#ffc107', description: 'Some practical experience' },
  { value: 4, label: 'Intermediate', color: '#ffeb3b', description: 'Comfortable with most tasks' },
  { value: 5, label: 'Competent', color: '#cddc39', description: 'Reliable performance' },
  { value: 6, label: 'Proficient', color: '#8bc34a', description: 'Consistently good results' },
  { value: 7, label: 'Advanced', color: '#4caf50', description: 'Advanced skills and judgment' },
  { value: 8, label: 'Expert', color: '#2196f3', description: 'Deep expertise and knowledge' },
  { value: 9, label: 'Master', color: '#3f51b5', description: 'Mastery and innovation' },
  { value: 10, label: 'Guru', color: '#9c27b0', description: 'Highest level of expertise' }
];

// ===============================================
// VALIDATION SCHEMA
// ===============================================

const skillValidationSchema = z.object({
  skill_code: z.string()
    .min(2, 'Code must be at least 2 characters')
    .max(50, 'Code must be less than 50 characters')
    .regex(/^[A-Z0-9_]+$/, 'Code must contain only uppercase letters, numbers, and underscores'),
  
  skill_name: z.string()
    .min(1, 'Skill name is required')
    .max(200, 'Name must be less than 200 characters'),
  
  description: z.string()
    .max(1000, 'Description must be less than 1000 characters')
    .nullable()
    .optional(),
  
  category: z.string()
    .min(1, 'Category is required'),
  
  subcategory: z.string()
    .max(100, 'Subcategory must be less than 100 characters')
    .nullable()
    .optional(),
  
  skill_type: z.enum(['capability', 'certification', 'equipment', 'restriction', 'preference'], {
    errorMap: () => ({ message: 'Skill type is required' })
  }),
  
  requires_certification: z.boolean(),
  
  certification_authority: z.string()
    .max(200, 'Authority must be less than 200 characters')
    .nullable()
    .optional(),
  
  certification_expiry_required: z.boolean(),
  
  certification_expiry_date: z.string()
    .optional()
    .nullable(),
  
  vroom_skill_id: z.number()
    .positive('VROOM ID must be positive')
    .nullable()
    .optional(),
    
  is_active: z.boolean().optional(),
  
  project_id: z.number().optional().nullable()
});

// ===============================================
// API INTEGRATION FUNCTIONS  
// ===============================================

const validateVehicleForJob = async (vehicleId: number, jobId: number): Promise<ValidationResult> => {
  try {
    const response = await fetch(`/api/skills/validate-assignment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
      },
      body: JSON.stringify({
        vehicle_id: vehicleId,
        job_id: jobId
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error validating vehicle-job assignment:', error);
    // Fallback to mock validation for development
    return {
      can_assign: true,
      missing_skills: [],
      warnings: []
    };
  }
};

const createSkill = async (skillData: SkillFormData & { project_id?: number }): Promise<Skill> => {
  try {
    // Get JWT token from Supabase auth
    const { supabase } = await import('../../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/skills`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: skillData.skill_name || skillData.name,
          description: skillData.description,
          skill_code: skillData.skill_code,
          category: skillData.category,
          subcategory: skillData.subcategory,
          parent_skill_id: skillData.parent_skill_id,
          skill_type: skillData.skill_type,
          requires_certification: skillData.requires_certification,
          certification_authority: skillData.certification_authority,
          certification_expiry_required: skillData.certification_expiry_required,
          vroom_skill_id: skillData.vroom_skill_id,
          project_id: skillData.project_id,
          is_active: true
        })
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        // If not JSON, use the text
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error creating skill:', error);
    throw error;
  }
};

const updateSkill = async (skillId: number, skillData: Partial<SkillFormData>): Promise<Skill> => {
  try {
    // Get JWT token from Supabase auth
    const { supabase } = await import('../../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/skills/${skillId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: skillData.skill_name || skillData.name,
          description: skillData.description,
          skill_code: skillData.skill_code,
          category: skillData.category,
          subcategory: skillData.subcategory,
          parent_skill_id: skillData.parent_skill_id,
          skill_type: skillData.skill_type,
          requires_certification: skillData.requires_certification,
          certification_authority: skillData.certification_authority,
          certification_expiry_required: skillData.certification_expiry_required,
          vroom_skill_id: skillData.vroom_skill_id,
          is_active: skillData.is_active !== undefined ? skillData.is_active : true
        })
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        // If not JSON, use the text
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating skill:', error);
    throw error;
  }
};

const deleteSkill = async (skillId: number): Promise<void> => {
  try {
    // Get JWT token from Supabase auth
    const { supabase } = await import('../../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/skills/${skillId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        // If not JSON, use the text
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Error deleting skill:', error);
    throw error;
  }
};

// ===============================================
// UTILITY FUNCTIONS
// ===============================================

const getProficiencyInfo = (level: number) => {
  return PROFICIENCY_LEVELS.find(p => p.value === level) || PROFICIENCY_LEVELS[0];
};

const getSkillTypeInfo = (type: string) => {
  return SKILL_TYPES.find(t => t.value === type) || SKILL_TYPES[0];
};

const calculateCompatibilityScore = (vehicleSkills: VehicleSkill[], requiredSkills: JobSkillRequirement[]): number => {
  if (requiredSkills.length === 0) return 100;
  
  let totalScore = 0;
  let skillCount = 0;
  
  requiredSkills.forEach(required => {
    const vehicleSkill = vehicleSkills.find(vs => vs.skill_id === required.skill_id);
    
    if (vehicleSkill) {
      const proficiencyRatio = Math.min(vehicleSkill.proficiency_level / required.minimum_proficiency_level, 1);
      const skillScore = proficiencyRatio * 100;
      
      if (required.is_mandatory) {
        totalScore += skillScore * 2; // Weight mandatory skills higher
        skillCount += 2;
      } else {
        totalScore += skillScore;
        skillCount += 1;
      }
    } else if (required.is_mandatory) {
      // Missing mandatory skill = 0 points
      skillCount += 2;
    } else {
      // Missing optional skill = partial credit
      totalScore += 25;
      skillCount += 1;
    }
  });
  
  return skillCount > 0 ? Math.round(totalScore / skillCount) : 0;
};

const isCertificationExpiring = (expiryDate: string): boolean => {
  const expiry = new Date(expiryDate);
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  return expiry <= thirtyDaysFromNow;
};

const isCertificationExpired = (expiryDate: string): boolean => {
  const expiry = new Date(expiryDate);
  const now = new Date();
  return expiry < now;
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const SkillsManager: React.FC<SkillsManagerProps> = ({
  projectId,
  selectionMode = false,
  onSkillsUpdate
}) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [selectedVehicle, setSelectedVehicle] = useState<number | null>(null);
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [createSkillOpen, setCreateSkillOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Unified VRP architecture - get scenario and dataset context
  const { currentScenario: selectedScenario, currentDataset: selectedDataset } = null /* TODO: Replace with Toolpad navigation */;
  
  // Use React Query hooks for data fetching
  const { data: skillsData = [], isLoading: skillsLoading, error: skillsError } = useSkills(projectId, {
    include_global: true,
    is_active: true
  });
  
  // Debug log to see what data we're getting
  console.log('🎯 [SkillsManager] Skills data from hook:', skillsData);
  console.log('🎯 [SkillsManager] Context data:', { selectedScenario, selectedDataset });
  
  // Try to pass scenario and dataset context, but fallback to loading all project vehicles if context missing
  const { data: vehicles = [], isLoading: vehiclesLoading, error: vehiclesError } = useVehicles(
    projectId, 
    selectedScenario?.id, 
    { 
      dataset_id: selectedDataset?.id,
      dataset_name: selectedDataset?.name,
      dataset_version: selectedDataset?.version,
      // Enable fallback to load all vehicles for this project if no context
      include_all_project_data: !selectedScenario?.id && !selectedDataset?.id
    }
  );
  
  const { data: jobs = [], isLoading: jobsLoading, error: jobsError } = useJobs(
    projectId,
    selectedScenario?.id,
    {
      dataset_id: selectedDataset?.id,
      dataset_name: selectedDataset?.name,
      dataset_version: selectedDataset?.version,
      // Enable fallback to load all jobs for this project if no context  
      include_all_project_data: !selectedScenario?.id && !selectedDataset?.id
    }
  );
  
  // Debug logs
  console.log('🎯 [SkillsManager] Vehicles data:', { vehicles, vehiclesLoading, vehiclesError });
  console.log('🎯 [SkillsManager] Jobs data:', { jobs, jobsLoading, jobsError });
  
  // Map skills data to component format
  const skills: Skill[] = skillsData.map((skill: SkillType) => ({
    ...skill,
    skill_name: skill.skill_name || skill.name, // Handle both field names
    name: skill.name || skill.skill_name, // Ensure compatibility
    usage_count: skill.vehicle_count || skill.job_count || skill.shipment_count || skill.total_usage || skill.usage_count || 0
  }));
  
  const loading = skillsLoading || vehiclesLoading || jobsLoading;
  const error = skillsError || vehiclesError || jobsError;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch
  } = useForm<SkillFormData>({
    resolver: zodResolver(skillValidationSchema),
    defaultValues: {
      skill_code: '',
      skill_name: '',
      description: '',
      category: '',
      subcategory: '',
      skill_type: 'capability',
      requires_certification: false,
      certification_expiry_required: false,
      certification_expiry_date: '',
      is_active: true,
      project_id: projectId
    },
    mode: 'onChange'
  });

  const currentStepId = MANAGER_STEPS[activeStep].id;

  // Validate vehicle-job compatibility
  useEffect(() => {
    const validateCompatibility = async () => {
      if (selectedVehicle && selectedJob) {
        setValidationLoading(true);
        try {
          const result = await validateVehicleForJob(selectedVehicle, selectedJob);
          setValidationResult(result);
        } catch (error) {
          console.error('Error validating compatibility:', error);
        } finally {
          setValidationLoading(false);
        }
      } else {
        setValidationResult(null);
      }
    };

    validateCompatibility();
  }, [selectedVehicle, selectedJob]);

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, MANAGER_STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  const handleCreateSkill = () => {
    reset();
    setEditingSkill(null);
    setCreateSkillOpen(true);
  };

  const handleEditSkill = (skill: Skill) => {
    setEditingSkill(skill);
    setValue('skill_code', skill.skill_code);
    setValue('skill_name', skill.skill_name);
    setValue('description', skill.description || '');
    setValue('category', skill.category);
    setValue('subcategory', skill.subcategory || '');
    setValue('skill_type', skill.skill_type);
    setValue('requires_certification', skill.requires_certification);
    setValue('certification_authority', skill.certification_authority || '');
    setValue('certification_expiry_required', skill.certification_expiry_required);
    setValue('certification_expiry_date', (skill as any).certification_expiry_date || '');
    setValue('vroom_skill_id', skill.vroom_skill_id);
    setValue('is_active', skill.is_active);
    setValue('project_id', skill.project_id);
    setCreateSkillOpen(true);
  };

  const handleSaveSkill = async (data: SkillFormData) => {
    try {
      setSaving(true);
      if (editingSkill) {
        // Update existing skill via API
        await updateSkill(editingSkill.id, data);
      } else {
        // Create new skill via API
        await createSkill({
          ...data,
          project_id: projectId,
          certification_expiry_date: data.certification_expiry_date || undefined
        });
      }
      
      setCreateSkillOpen(false);
      setEditingSkill(null);
      reset();
      onSkillsUpdate?.();
      // The useSkills hook will automatically refetch data
    } catch (error) {
      console.error('Error saving skill:', error);
      alert(`Failed to save skill: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSkill = async (skillId: number) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      try {
        setSaving(true);
        await deleteSkill(skillId);
        onSkillsUpdate?.();
        // The useSkills hook will automatically refetch data
      } catch (error) {
        console.error('Error deleting skill:', error);
        alert(`Failed to delete skill: ${error.message}`);
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading skills management...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Error loading data: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {MANAGER_STEPS.map((step, index) => (
          <Step key={step.id} onClick={() => handleStepClick(index)} sx={{ cursor: 'pointer' }}>
            <StepLabel icon={step.icon}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {/* Step 1: Skills Taxonomy */}
          {currentStepId === 'taxonomy' && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <Psychology />
                  Skills Taxonomy Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateSkill}
                >
                  Create Skill
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Manage your skills taxonomy to define the capabilities, certifications, and requirements for your fleet.
              </Alert>

              {/* Skills Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="primary">{skills.length}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Skills</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="secondary">
                        {skills.filter(s => s.requires_certification).length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Certifications</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="success.main">
                        {[...new Set(skills.map(s => s.category))].length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Categories</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="warning.main">
                        {skills.reduce((sum, skill) => sum + (skill.usage_count || 0), 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Usage</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Skills Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Skill</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="center">Certification</TableCell>
                      <TableCell align="center">Usage</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skills.map((skill) => (
                      <TableRow key={skill.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {skill.skill_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {skill.skill_code}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={skill.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getSkillTypeInfo(skill.skill_type).label}
                            size="small"
                            color={skill.skill_type === 'certification' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {skill.requires_certification ? (
                            <Verified color="primary" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Badge badgeContent={skill.usage_count || 0} color="primary">
                            <TrendingUp color="action" />
                          </Badge>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditSkill(skill)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSkill(skill.id)}
                            disabled={skill.usage_count && skill.usage_count > 0}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Step 2: Vehicle Assignments */}
          {currentStepId === 'assignments' && (
            <Box>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Assignment />
                Vehicle Skills Assignment
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                Assign skills to your vehicles and set proficiency levels. Certifications with expiry dates will be tracked automatically.
              </Alert>

              {vehicles.map((vehicle) => (
                <Accordion key={vehicle.id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={2} flex={1}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <LocalShipping />
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="subtitle1">{vehicle.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vehicle.license_plate} • {vehicle.skills?.length || 0} skills assigned
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        {vehicle.skills?.slice(0, 3).map((vs) => {
                          const profInfo = getProficiencyInfo(vs.proficiency_level);
                          return (
                            <Chip
                              key={vs.id}
                              label={vs.skill.skill_name}
                              size="small"
                              style={{ backgroundColor: profInfo.color, color: '#fff' }}
                            />
                          );
                        })}
                        {vehicle.skills && vehicle.skills.length > 3 && (
                          <Chip label={`+${vehicle.skills.length - 3}`} size="small" />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Add New Skill
                        </Typography>
                        <Autocomplete
                          options={skills.filter(skill => 
                            !vehicle.skills?.some(vs => vs.skill_id === skill.id)
                          )}
                          getOptionLabel={(option) => `${option.skill_name} (${option.category})`}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box display="flex" alignItems="center" gap={1} width="100%">
                                <Box flex={1}>
                                  <Typography variant="body2">{option.skill_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.category}
                                    {option.requires_certification && ' • Certification Required'}
                                  </Typography>
                                </Box>
                                <Chip 
                                  label={getSkillTypeInfo(option.skill_type).label}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search skills to assign..."
                              size="small"
                            />
                          )}
                          onChange={async (_, value) => {
                            if (value) {
                              try {
                                console.log('🎯 Assigning skill to vehicle:', { vehicle_id: vehicle.id, skill_id: value.id });
                                await apiClient.assignSkillToVehicle({
                                  vehicle_id: vehicle.id,
                                  skill_id: value.id,
                                  proficiency_level: 5, // Default intermediate level
                                  verified: false
                                });
                                // Refresh vehicles data to show the new assignment
                                window.location.reload(); // Simple refresh for now
                              } catch (error) {
                                console.error('❌ Failed to assign skill:', error);
                                alert(`Failed to assign skill: ${error.message}`);
                              }
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                          Assigned Skills
                        </Typography>
                        {vehicle.skills && vehicle.skills.length > 0 ? (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Skill</TableCell>
                                  <TableCell>Proficiency</TableCell>
                                  <TableCell>Certification</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {vehicle.skills.map((vs) => {
                                  const profInfo = getProficiencyInfo(vs.proficiency_level);
                                  const isExpiring = vs.certification_expiry_date && 
                                    isCertificationExpiring(vs.certification_expiry_date);
                                  const isExpired = vs.certification_expiry_date && 
                                    isCertificationExpired(vs.certification_expiry_date);
                                  
                                  return (
                                    <TableRow key={vs.id}>
                                      <TableCell>
                                        <Box>
                                          <Typography variant="body2">{vs.skill.skill_name}</Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {vs.skill.category}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <Chip
                                            label={`${profInfo.label} (${vs.proficiency_level})`}
                                            size="small"
                                            style={{ backgroundColor: profInfo.color, color: '#fff' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        {vs.certification_number ? (
                                          <Box>
                                            <Typography variant="caption">{vs.certification_number}</Typography>
                                            {vs.certification_expiry_date && (
                                              <Typography variant="caption" display="block" color="text.secondary">
                                                Expires: {new Date(vs.certification_expiry_date).toLocaleDateString()}
                                              </Typography>
                                            )}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                          {vs.verified ? (
                                            <CheckCircle color="success" fontSize="small" />
                                          ) : (
                                            <Warning color="warning" fontSize="small" />
                                          )}
                                          {isExpired && (
                                            <Chip label="Expired" size="small" color="error" />
                                          )}
                                          {isExpiring && !isExpired && (
                                            <Chip label="Expiring" size="small" color="warning" />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center">
                                        <IconButton size="small">
                                          <Edit />
                                        </IconButton>
                                        <IconButton 
                                          size="small" 
                                          color="error"
                                          onClick={async () => {
                                            try {
                                              console.log('🗑️ Removing skill from vehicle:', vs.id);
                                              await apiClient.removeSkillFromVehicle(vs.id);
                                              // Refresh vehicles data to remove the assignment
                                              window.location.reload(); // Simple refresh for now
                                            } catch (error) {
                                              console.error('❌ Failed to remove skill:', error);
                                              alert(`Failed to remove skill: ${error.message}`);
                                            }
                                          }}
                                        >
                                          <Delete />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Alert severity="warning">
                            No skills assigned to this vehicle. Add skills to enable intelligent routing.
                          </Alert>
                        )}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Step 3: Skill Validation */}
          {currentStepId === 'validation' && (
            <Box>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Verified />
                Skill Validation & Compatibility
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                Test vehicle-job compatibility to ensure your assignments will work correctly during optimization.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Vehicle</InputLabel>
                    <Select
                      value={selectedVehicle || ''}
                      onChange={(e) => setSelectedVehicle(e.target.value as number)}
                      label="Select Vehicle"
                    >
                      {vehicles.map((vehicle) => (
                        <MenuItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.name} ({vehicle.license_plate}) - {vehicle.skills?.length || 0} skills
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Job</InputLabel>
                    <Select
                      value={selectedJob || ''}
                      onChange={(e) => setSelectedJob(e.target.value as number)}
                      label="Select Job"
                    >
                      {jobs.map((job) => (
                        <MenuItem key={job.id} value={job.id}>
                          {job.reference} - {job.required_skills?.length || 0} required skills
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedVehicle && selectedJob && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                          <Assessment />
                          Compatibility Analysis
                          {validationLoading && <CircularProgress size={20} />}
                        </Typography>

                        {validationResult && !validationLoading && (
                          <Box>
                            {/* Compatibility Score */}
                            <Box mb={3}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2">Compatibility Score</Typography>
                                <Typography variant="h6" color={validationResult.can_assign ? 'success.main' : 'error.main'}>
                                  {(() => {
                                    const vehicle = vehicles.find(v => v.id === selectedVehicle);
                                    const job = jobs.find(j => j.id === selectedJob);
                                    if (vehicle && job) {
                                      const score = calculateCompatibilityScore(
                                        vehicle.skills || [],
                                        job.required_skills || []
                                      );
                                      return `${score}%`;
                                    }
                                    return validationResult.can_assign ? '100%' : '0%';
                                  })()}
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={(() => {
                                  const vehicle = vehicles.find(v => v.id === selectedVehicle);
                                  const job = jobs.find(j => j.id === selectedJob);
                                  if (vehicle && job) {
                                    return calculateCompatibilityScore(
                                      vehicle.skills || [],
                                      job.required_skills || []
                                    );
                                  }
                                  return validationResult.can_assign ? 100 : 0;
                                })()}
                                color={validationResult.can_assign ? 'success' : 'error'}
                              />
                            </Box>

                            {/* Assignment Status */}
                            <Alert 
                              severity={validationResult.can_assign ? 'success' : 'error'}
                              sx={{ mb: 2 }}
                            >
                              {validationResult.can_assign 
                                ? 'Vehicle can be assigned to this job'
                                : 'Vehicle cannot be assigned to this job due to missing required skills'
                              }
                            </Alert>

                            {/* Missing Skills */}
                            {validationResult.missing_skills.length > 0 && (
                              <Box mb={2}>
                                <Typography variant="subtitle2" gutterBottom color="error">
                                  Missing Required Skills
                                </Typography>
                                <List dense>
                                  {validationResult.missing_skills.map((missing, index) => (
                                    <ListItem key={index}>
                                      <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'error.main' }}>
                                          <Error />
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText
                                        primary={missing.skill_name}
                                        secondary={
                                          <Box>
                                            <Typography variant="caption">
                                              Required Level: {missing.required_level}, 
                                              Current Level: {missing.current_level}
                                            </Typography>
                                            {missing.certification_expired && (
                                              <Chip 
                                                label="Certification Expired" 
                                                size="small" 
                                                color="error" 
                                                sx={{ ml: 1 }} 
                                              />
                                            )}
                                          </Box>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}

                            {/* Warnings */}
                            {validationResult.warnings.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom color="warning.main">
                                  Warnings
                                </Typography>
                                {validationResult.warnings.map((warning, index) => (
                                  <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                                    {warning.message}
                                  </Alert>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Step 4: Analytics */}
          {currentStepId === 'analytics' && (
            <Box>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Analytics />
                Skills Analytics & Insights
              </Typography>
              
              <Grid container spacing={3}>
                {/* Skills Distribution */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Skills by Category
                      </Typography>
                      {SKILL_CATEGORIES.map((category) => {
                        const categorySkills = skills.filter(s => s.category === category);
                        const percentage = skills.length > 0 ? (categorySkills.length / skills.length) * 100 : 0;
                        
                        return (
                          <Box key={category} mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">{category}</Typography>
                              <Typography variant="body2">{categorySkills.length}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={percentage} />
                          </Box>
                        );
                      })}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Vehicle Coverage */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Vehicle Skills Coverage
                      </Typography>
                      {vehicles.map((vehicle) => {
                        const coverage = skills.length > 0 ? 
                          ((vehicle.skills?.length || 0) / skills.length) * 100 : 0;
                        
                        return (
                          <Box key={vehicle.id} mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">{vehicle.name}</Typography>
                              <Typography variant="body2">
                                {vehicle.skills?.length || 0}/{skills.length} skills
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={coverage}
                              color={coverage > 70 ? 'success' : coverage > 40 ? 'warning' : 'error'}
                            />
                          </Box>
                        );
                      })}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Certification Status */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Certification Status Overview
                      </Typography>
                      <Grid container spacing={2}>
                        {vehicles.map((vehicle) => {
                          const certifications = vehicle.skills?.filter(vs => 
                            vs.skill.requires_certification && vs.certification_expiry_date
                          ) || [];
                          
                          const expiring = certifications.filter(vs => 
                            vs.certification_expiry_date && isCertificationExpiring(vs.certification_expiry_date)
                          );
                          
                          const expired = certifications.filter(vs => 
                            vs.certification_expiry_date && isCertificationExpired(vs.certification_expiry_date)
                          );
                          
                          return (
                            <Grid item xs={12} md={4} key={vehicle.id}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {vehicle.name}
                                </Typography>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                  <Chip 
                                    label={`${certifications.length} Total`} 
                                    size="small" 
                                    color="primary" 
                                  />
                                  {expiring.length > 0 && (
                                    <Chip 
                                      label={`${expiring.length} Expiring`} 
                                      size="small" 
                                      color="warning" 
                                    />
                                  )}
                                  {expired.length > 0 && (
                                    <Chip 
                                      label={`${expired.length} Expired`} 
                                      size="small" 
                                      color="error" 
                                    />
                                  )}
                                </Box>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 5: Review */}
          {currentStepId === 'review' && (
            <Box>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Check />
                Skills Management Review
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Skills Summary
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Skills:</strong> {skills.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Categories:</strong> {[...new Set(skills.map(s => s.category))].length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Certifications:</strong> {skills.filter(s => s.requires_certification).length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Active Skills:</strong> {skills.filter(s => s.is_active).length}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Vehicle Coverage
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Vehicles:</strong> {vehicles.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Vehicles with Skills:</strong> {vehicles.filter(v => v.skills && v.skills.length > 0).length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Assignments:</strong> {vehicles.reduce((sum, v) => sum + (v.skills?.length || 0), 0)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Average Skills per Vehicle:</strong> {
                        vehicles.length > 0 
                          ? (vehicles.reduce((sum, v) => sum + (v.skills?.length || 0), 0) / vehicles.length).toFixed(1)
                          : 0
                      }
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      System Health
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Chip 
                        label="Skills Taxonomy Complete" 
                        color="success" 
                        icon={<CheckCircle />} 
                      />
                      <Chip 
                        label={`${vehicles.filter(v => v.skills && v.skills.length > 0).length}/${vehicles.length} Vehicles Configured`}
                        color={vehicles.filter(v => v.skills && v.skills.length > 0).length === vehicles.length ? 'success' : 'warning'}
                        icon={<Assignment />}
                      />
                      <Chip 
                        label="Validation Ready" 
                        color="info" 
                        icon={<Verified />} 
                      />
                      <Chip 
                        label="Analytics Available" 
                        color="info" 
                        icon={<Analytics />} 
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>

        <Divider />

        {/* Navigation Buttons */}
        <Box p={2} display="flex" justifyContent="space-between">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBefore />}
          >
            Back
          </Button>

          <Button
            disabled={activeStep === MANAGER_STEPS.length - 1}
            variant="contained"
            onClick={handleNext}
            endIcon={<NavigateNext />}
          >
            Next
          </Button>
        </Box>
      </Card>

      {/* Create/Edit Skill Dialog */}
      <Dialog 
        open={createSkillOpen} 
        onClose={() => setCreateSkillOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(handleSaveSkill)}>
          <DialogTitle>
            {editingSkill ? 'Edit Skill' : 'Create New Skill'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="skill_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Skill Code"
                      fullWidth
                      required
                      error={!!errors.skill_code}
                      helperText={errors.skill_code?.message}
                      placeholder="e.g., CDL_A, FORKLIFT"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="skill_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Skill Name"
                      fullWidth
                      required
                      error={!!errors.skill_name}
                      helperText={errors.skill_name?.message}
                      placeholder="e.g., Commercial Driver License Class A"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      multiline
                      rows={2}
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="Brief description of the skill..."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={SKILL_CATEGORIES}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value || '')}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Category"
                          required
                          error={!!errors.category}
                          helperText={errors.category?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="subcategory"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Subcategory"
                      fullWidth
                      error={!!errors.subcategory}
                      helperText={errors.subcategory?.message}
                      placeholder="Optional subcategory"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="skill_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required>
                      <InputLabel>Skill Type</InputLabel>
                      <Select
                        {...field}
                        label="Skill Type"
                        error={!!errors.skill_type}
                      >
                        {SKILL_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box>
                              <Typography variant="body2">{type.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {type.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="vroom_skill_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      label="VROOM Skill ID"
                      type="number"
                      fullWidth
                      error={!!errors.vroom_skill_id}
                      helperText={errors.vroom_skill_id?.message || "Numeric ID used in VROOM optimization"}
                      placeholder="Optional numeric ID"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="requires_certification"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Requires Certification</Typography>
                          <Typography variant="caption" color="text.secondary">
                            This skill requires official certification or licensing
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>

              {/* Certification Fields - only show if requires_certification is true */}
              {watch('requires_certification') && (
                <>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="certification_authority"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Certification Authority"
                          fullWidth
                          error={!!errors.certification_authority}
                          helperText={errors.certification_authority?.message}
                          placeholder="e.g., DOT, OSHA, Company Training Dept"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="certification_expiry_required"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Certification Expires</Typography>
                              <Typography variant="caption" color="text.secondary">
                                This certification has an expiry date that needs tracking
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                  </Grid>

                  {watch('certification_expiry_required') && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="certification_expiry_date"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Certification Expiry Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.certification_expiry_date}
                            helperText={errors.certification_expiry_date?.message || "When this certification expires"}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateSkillOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <Check />}
            >
              {saving ? 'Saving...' : (editingSkill ? 'Update Skill' : 'Create Skill')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export { SkillsManager };